import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppRail } from '@/components/AppRail';
import { LiveEventsToast } from '@/components/LiveEventsToast';
import { useMe } from '@/lib/hooks';
import { ApiError } from '@/lib/api';
import { useRunsSync } from '@/lib/runs';
import { dirtyTracking, useLiveSync } from '@/lib/live';
import { LiveBanners } from '@/components/LiveBanners';

/**
 * Authenticated shell — checks /api/auth/me; if unauthenticated, bounces
 * to /login. Otherwise renders the icon rail + <Outlet />.
 */
export function App() {
  const location = useLocation();
  const meQuery = useMe();

  if (meQuery.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Lade Session…
      </div>
    );
  }

  const unauth = meQuery.error instanceof ApiError && meQuery.error.status === 401;
  if (unauth || !meQuery.data) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Global matters (Admin-Panel, Zugriff overview, Live Log, bot restart) are
  // admin-only; per-server managers use Verwaltung → Zugriff in the server.
  const isAdmin = meQuery.data.isAdmin;

  return (
    <div className="flex h-screen">
      <AppRail user={meQuery.data.user} isAdmin={isAdmin} />

      {/* ServerLayout cancels this padding with matching negative margins.
          Input in here counts as unsaved (live updates hold form data). */}
      <main className="min-w-0 flex-1 overflow-y-auto p-3 md:p-8" {...dirtyTracking}>
        <Outlet />
      </main>

      <LiveEventsToast />
      <LiveBanners />
      <LiveSync />
    </div>
  );
}

/** Keeps every page live: changes of others, bot results, backup runs (lib/live.ts, lib/runs.ts). */
function LiveSync() {
  useLiveSync();
  useRunsSync();
  return null;
}
